import React, { useState, useEffect } from 'react';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { createReactBlockSpec } from "@blocknote/react";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import {
  UploadFile as UploadFileIcon,
  Settings as SettingsIcon,
  Description as DescriptionIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  TipsAndUpdates as TipsAndUpdatesIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import "./styles.css";
import DocumentUploadReviewModal from './DocumentUploadReviewModal';
import LoadSpinner from '../../../utilities/LoadSpinner';
import { getRightworkAPI, postRightworkAPI } from '../requests/utilities/requests';
import {
  API_get_file_upload,
  API_post_learning_document_upload_completion,
  API_get_learning_document_upload_completion
} from '../../../constants';
import { useNotifyMobileModal } from '../take_training_employee/useNotifyMobileModal';

export const DocumentUpload = (props) => {
  const { block, editor } = props;

  // Config from block props - use documentUploadBlockData if available, otherwise direct props
  const blockData = block.props.documentUploadBlockData || {};
  const [config, setConfig] = useState({
    title: blockData.title || block.props.title || "Upload Document",
    description: blockData.description || block.props.description || "",
    required: blockData.required !== undefined ? blockData.required : (block.props.required !== undefined ? block.props.required : true),
    requires_approval: blockData.requires_approval !== undefined ? blockData.requires_approval : (block.props.requires_approval !== undefined ? block.props.requires_approval : false),
    save_to_profile: blockData.save_to_profile !== undefined ? blockData.save_to_profile : (block.props.save_to_profile !== undefined ? block.props.save_to_profile : true),
    certificateTypeSlug: blockData.certificate_type_slug !== undefined ? (blockData.certificate_type_slug || "") : (block.props.certificateTypeSlug || ""),
  });

  // Catalog options for the mapping picker (edit mode only). SectionInputsModal2
  // exposes a deferred ({promise, resolve}) in a ref, created synchronously on
  // its first render so it exists even when this block mounts in the same
  // commit (the BlockNote schema closure is frozen on first render, so a plain
  // prop would stay empty). Awaiting it here and mirroring into state
  // guarantees this block re-renders when the catalog lands, even if the
  // settings panel was opened while the fetch was in flight.
  const [certificateTypeOptions, setCertificateTypeOptions] = useState([]);
  const [certificateTypeOptionsLoaded, setCertificateTypeOptionsLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const promise = props.certificateTypeOptionsPromiseRef?.current?.promise;
    if (promise) {
      promise.then((options) => {
        if (!cancelled) {
          setCertificateTypeOptions(options || []);
          setCertificateTypeOptionsLoaded(true);
        }
      });
    }
    return () => {
      cancelled = true;
    };
  }, [props.certificateTypeOptionsPromiseRef]);
  const selectedCertificateType = certificateTypeOptions.find(
    (t) => t.value === config.certificateTypeSlug
  );

  // State for completion data
  const [completionData, setCompletionData] = useState(null);
  const [isLoadingCompletion, setIsLoadingCompletion] = useState(false);
  const [uploadedFileUrl, setUploadedFileUrl] = useState(null);

  // Check if document is already uploaded
  const isCompleted = Boolean(completionData?.is_completed);

  // Open by default in edit mode so it's obvious the block is configurable
  // (only rendered there; complete mode never shows the panel).
  const [showSettings, setShowSettings] = useState(true);
  const [openUploadModal, setOpenUploadModal] = useState(false);
  useNotifyMobileModal(openUploadModal);

  const updateConfig = (newConfig) => {
    setConfig(newConfig);
    // Update the block props in the editor
    editor.updateBlock(block, {
      type: "documentUpload",
      props: {
        ...block.props,
        ...newConfig,
      },
    });
  };

  // Use completion status from block props (now included in section data)
  useEffect(() => {
    const loadCompletionStatus = async () => {
      const blockData = block.props.documentUploadBlockData;

      if (props.mode === "complete" && !props.isPreview && blockData) {
        setIsLoadingCompletion(true);
        try {
          // Completion status is now included in the block data from the backend
          const isCompleted = blockData.user_has_completed === true;

          setCompletionData({
            is_completed: isCompleted,
            document_upload_completion: isCompleted ? {
              uploaded_file_uuid: blockData.uploaded_file_uuid,
              created_at: blockData.completed_at,
              file_name: blockData.file_name,
            } : null,
          });

          // If completed, fetch the uploaded file URL (if file still exists)
          if (isCompleted && blockData.uploaded_file_uuid) {
            try {
              const resFile = await getRightworkAPI(API_get_file_upload, {
                file_upload_uuid: blockData.uploaded_file_uuid,
              });
              const freshUrl = resFile?.data?.redux?.payload?.presigned_url;
              if (freshUrl) {
                setUploadedFileUrl(freshUrl);
              }
            } catch (error) {
              // File no longer exists - completion record exists but file is gone
              console.warn('Uploaded file no longer available:', error);
            }
          }
        } catch (error) {
          console.error('Error loading document upload completion status:', error);
        } finally {
          setIsLoadingCompletion(false);
        }
      }
    };

    loadCompletionStatus();
  }, [props.mode, props.isPreview, block.props.documentUploadBlockData, props.sectionUUID]);

  // Handle completion of document upload by employee
  const handleUploadCompletion = async (uploadedFileData, expirationDate) => {
    try {
      const documentBlockUUID = block.props.documentUploadBlockData?.uuid;
      if (!documentBlockUUID) {
        console.error('No document upload block UUID found');
        toast.error('Configuration error. Please contact your administrator.');
        return;
      }

      const params = {
        document_block_uuid: documentBlockUUID,
        section_uuid: props.sectionUUID,
        uploaded_file_uuid: uploadedFileData.uuid,
        company_id: props.homeCompany,
      };

      // Expiration date for certificate-mapped blocks (YYYY-MM-DD)
      if (expirationDate) {
        params.expiration_date = expirationDate;
      }

      // Only include store_team_member_id if defined
      if (props.employeeID) {
        params.store_team_member_id = props.employeeID;
      }

      const response = await postRightworkAPI(API_post_learning_document_upload_completion, params);

      const payload = response?.data?.redux?.payload;

      // Update local completion state
      setCompletionData({
        is_completed: true,
        document_upload_completion: payload?.document_upload_completion,
      });

      // Fetch the uploaded file URL
      const resFile = await getRightworkAPI(API_get_file_upload, {
        file_upload_uuid: uploadedFileData.uuid,
      });
      const freshUrl = resFile?.data?.redux?.payload?.presigned_url;
      if (freshUrl) {
        setUploadedFileUrl(freshUrl);
      }

      setOpenUploadModal(false);
    } catch (error) {
      console.error('Error saving document upload completion:', error);
      toast.error('Failed to save upload completion. Please try again.');
      throw error; // Re-throw so modal knows it failed
    }
  };

  // Handle viewing uploaded document
  const handleViewUploaded = () => {
    if (uploadedFileUrl) {
      window.open(uploadedFileUrl, '_blank');
    } else if (isCompleted) {
      // Completion exists but file is missing
      toast.error('The uploaded document is no longer available.');
    }
  };


  // Complete/Preview mode - Shows the actual upload interface
  if (props.mode === "complete") {
    // If this is a preview (manager viewing in track/module preview), show static view
    const isPreview = props.isPreview === true;

    const handleCardClick = () => {
      // In preview mode, don't allow interaction
      if (isPreview || isLoadingCompletion) return;

      if (isCompleted) {
        handleViewUploaded();
      } else {
        setOpenUploadModal(true);
      }
    };

    const getTranslation = (key) => {
      const translations = {
        uploadDocument: {
          en: 'Upload Document',
          es: 'Subir Documento'
        },
        clickToUpload: {
          en: 'Click to upload',
          es: 'Haz clic para subir'
        },
        viewDocument: {
          en: 'View Uploaded Document',
          es: 'Ver Documento Subido'
        },
        required: {
          en: 'Required',
          es: 'Requerido'
        },
        completed: {
          en: 'Completed',
          es: 'Completado'
        }
      };

      return translations[key]?.[props.language] || translations[key]?.en || key;
    };

    // If preview mode, show the same preview as in the configuration panel
    if (isPreview) {
      return (
        <div className="w-full bg-white rounded-lg border border-slate-200 p-4" contentEditable={false}>
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-violet-100">
              <CloudUploadIcon className="text-violet-600" style={{ fontSize: 20 }} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-base font-semibold text-gray-900">
                  {config.title}
                </span>
                {config.required && (
                  <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded">
                    Required
                  </span>
                )}
              </div>

              {config.description && (
                <p className="text-gray-600 mt-1" style={{ fontSize: '14px' }}>
                  {config.description}
                </p>
              )}

              {/* Mock upload button */}
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-700 text-white rounded-lg text-sm font-medium mb-2">
                  <UploadFileIcon style={{ fontSize: 18 }} />
                  Choose File to Upload
                </div>
                <p className="text-gray-500" style={{ fontSize: '12px' }}>
                  (Employee upload interface - preview only)
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Active employee view
    return (
      <>
        <div
          className={`w-full border rounded-lg transition-all ${
            isCompleted
              ? 'border-emerald-200 bg-emerald-50 hover:bg-emerald-100 cursor-pointer'
              : 'border-slate-200 bg-white hover:border-slate-300 cursor-pointer'
          }`}
          contentEditable={false}
          onClick={handleCardClick}
        >
          {isLoadingCompletion ? (
            <div className="px-4 py-6 flex items-center justify-center">
              <LoadSpinner size={24} />
            </div>
          ) : (
            <div className="px-4 py-4">
              <div className="flex items-start gap-3">
                {isCompleted ? (
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-100">
                    <CheckCircleIcon className="text-emerald-600" style={{ fontSize: 20 }} />
                  </div>
                ) : (
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-violet-100">
                    <CloudUploadIcon className="text-violet-600" style={{ fontSize: 20 }} />
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-base font-semibold text-gray-900">
                      {config.title}
                    </span>
                    {config.required && !isCompleted && (
                      <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded">
                        {getTranslation('required')}
                      </span>
                    )}
                    {isCompleted && (
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">
                        {getTranslation('completed')}
                      </span>
                    )}
                  </div>

                  {config.description && (
                    <p className="text-gray-600 mt-1" style={{ fontSize: '14px' }}>
                      {config.description}
                    </p>
                  )}

                  <div className={`mt-3 pt-3 border-t ${isCompleted ? "border-emerald-100" : "border-gray-100"}`}>
                    {isCompleted ? (
                      <>
                        <p className="text-emerald-600 mb-2 font-medium" style={{ fontSize: 12 }}>
                          Completed on {new Date(completionData?.document_upload_completion?.created_at).toLocaleDateString()}
                        </p>
                        <div className="flex items-center gap-2">
                          <DescriptionIcon className="text-emerald-600" style={{ fontSize: 20 }} />
                          <span className="text-sm text-gray-700">
                            {completionData?.document_upload_completion?.file_name || 'Document uploaded'}
                          </span>
                        </div>
                      </>
                    ) : (
                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-700 text-white rounded-lg text-sm font-medium">
                        <UploadFileIcon style={{ fontSize: 18 }} />
                        {getTranslation('clickToUpload')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Upload Modal */}
        <DocumentUploadReviewModal
          open={openUploadModal}
          onClose={() => setOpenUploadModal(false)}
          config={config}
          homeCompany={props.homeCompany}
          onUploadComplete={handleUploadCompletion}
          language={props.language}
          // Certificate-mapped blocks that track expiration collect the date
          // at upload time (hydrated server-side into documentUploadBlockData).
          requiresExpirationDate={
            Boolean(blockData.certificate_type_slug) &&
            blockData.certificate_type_has_expiration === true
          }
          certificateTypeLabel={blockData.certificate_type_label}
        />
      </>
    );
  }

  // Edit mode - Shows configuration interface (manager side)
  return (
    <div
      className="w-full border-2 border-gray-200 rounded-lg bg-gray-50 outline-none focus:outline-none"
      contentEditable={false}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200 rounded-t-lg">
        <div className="flex items-center gap-2">
          <UploadFileIcon className="text-violet-700" style={{ fontSize: 24 }} />
          <div>
            <div className="text-sm font-semibold text-gray-700">
              Employee Document Upload
            </div>
            <div className="text-xs text-gray-500">
              Employees will upload files here
            </div>
          </div>
        </div>

        {/* Settings Toggle Button */}
        <button
          className="cursor-pointer hover:bg-gray-200 p-2 rounded transition-colors flex items-center gap-1"
          onClick={() => setShowSettings(!showSettings)}
          type="button"
        >
          <SettingsIcon className="text-violet-700" style={{ fontSize: 20 }} />
          {showSettings ? (
            <ExpandLessIcon className="text-violet-700" style={{ fontSize: 18 }} />
          ) : (
            <ExpandMoreIcon className="text-violet-700" style={{ fontSize: 18 }} />
          )}
        </button>
      </div>

      {/* Custom Settings Panel */}
      {showSettings && (
        <div className="px-4 py-4 bg-white border-b border-gray-200">
          <h4 className="text-sm font-semibold text-gray-900 mb-4">Configure Document Upload</h4>

          {/* Title Input */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-700 mb-1.5 mt-1">
              Title
            </label>
            <input
              type="text"
              placeholder="e.g., Upload Certificate of Completion"
              value={config.title}
              onChange={(e) => updateConfig({ ...config, title: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-700 focus:border-transparent"
            />
          </div>

          {/* Description Textarea */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Instructions
            </label>
            <textarea
              placeholder="Tell employees what document to upload and any requirements..."
              value={config.description}
              onChange={(e) => updateConfig({ ...config, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-700 focus:border-transparent resize-none"
            />
          </div>

          <div className="border-t border-gray-200 my-4"></div>

          {/* Required Switch */}
          <div className="mb-4">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <div className="text-sm font-medium text-gray-900">Require to complete training</div>
                <div className="text-xs text-gray-500">Employees must upload to proceed</div>
              </div>
              <div className="relative">
                <input
                  type="checkbox"
                  checked={config.required}
                  onChange={(e) => updateConfig({ ...config, required: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-700"></div>
              </div>
            </label>
          </div>

          {/* Save to employee profile switch. Locked ON while the block maps to
              a document type: the upload becomes a Certificate on the profile
              regardless, so an off toggle would lie. */}
          <div className="mb-4">
            <label
              className={`flex items-center justify-between ${
                config.certificateTypeSlug ? "cursor-not-allowed opacity-60" : "cursor-pointer"
              }`}
            >
              <div>
                <div className="text-sm font-medium text-gray-900">Keep a copy on the employee's profile</div>
                <div className="text-xs text-gray-500">
                  {config.certificateTypeSlug
                    ? "Always on for uploads saved to employee documents"
                    : "Stays on their profile even after the training changes or is unassigned"}
                </div>
              </div>
              <div className="relative">
                <input
                  type="checkbox"
                  checked={config.certificateTypeSlug ? true : config.save_to_profile}
                  disabled={Boolean(config.certificateTypeSlug)}
                  onChange={(e) => updateConfig({ ...config, save_to_profile: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-700"></div>
              </div>
            </label>
          </div>

          {/* Certificate-type mapping. Inline font sizes throughout: BlockNote's
              editor styles override Tailwind text-size utilities on p/label/
              select elements inside the container (same reason the rest of this
              file uses style={{ fontSize }}). */}
          <div className="mb-4">
            <label
              className="block font-medium text-gray-700 mb-1.5"
              style={{ fontSize: "12px" }}
            >
              Also save to employee documents as
            </label>
            <Select
              fullWidth
              displayEmpty
              size="small"
              value={config.certificateTypeSlug}
              onChange={(e) =>
                updateConfig({
                  ...config,
                  certificateTypeSlug: e.target.value,
                  // A mapped upload becomes a Certificate, which always shows
                  // on the employee profile - keep the toggle truthful.
                  ...(e.target.value ? { save_to_profile: true } : {}),
                })
              }
              renderValue={(value) => {
                if (!value) return "None";
                if (selectedCertificateType) return selectedCertificateType.label;
                return (
                  (blockData.certificate_type_label || value) +
                  (certificateTypeOptionsLoaded ? " (no longer available)" : "")
                );
              }}
              sx={{
                borderRadius: "8px",
                height: "45px",
                backgroundColor: "white",
                fontSize: "14px",
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#6d28d9",
                },
              }}
            >
              <MenuItem value="">None</MenuItem>
              {/* A mapping whose type is missing from the options: while the
                  catalog is still loading, show it plainly (it may well be
                  valid); once loaded, mark it as archived/hidden (disabled) so
                  it doesn't silently vanish. */}
              {config.certificateTypeSlug && !selectedCertificateType && (
                <MenuItem
                  value={config.certificateTypeSlug}
                  disabled={certificateTypeOptionsLoaded}
                >
                  {blockData.certificate_type_label || config.certificateTypeSlug}
                  {certificateTypeOptionsLoaded ? " (no longer available)" : ""}
                </MenuItem>
              )}
              {certificateTypeOptions.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
            <div className="text-gray-500 mt-1.5" style={{ fontSize: "12px" }}>
              {config.certificateTypeSlug
                ? selectedCertificateType?.has_expiration
                  ? "Uploads will appear on the Documents overview. Employees will be asked for the document's expiration date when they upload."
                  : "Uploads will appear on the Documents overview as this document type."
                : "Optional: link uploads to a document type so they count toward Documents tracking."}
            </div>
          </div>

          {/* Requires Approval Switch - COMMENTED OUT FOR NOW */}
          {/* <div className="mb-0">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <div className="text-sm font-medium text-gray-900">Require manager approval</div>
                <div className="text-xs text-gray-500">Manager must approve upload before completion</div>
              </div>
              <div className="relative">
                <input
                  type="checkbox"
                  checked={config.requires_approval}
                  onChange={(e) => updateConfig({ ...config, requires_approval: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-700"></div>
              </div>
            </label>
          </div> */}
        </div>
      )}

      {/* Preview of what employees will see */}
      <div className="px-4 py-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-start gap-3">
            <DescriptionIcon className="text-gray-400 mt-1" style={{ fontSize: 32 }} />
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-base font-semibold text-gray-900">
                  {config.title}
                </span>
                {config.required && (
                  <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded">
                    Required
                  </span>
                )}
                {config.requires_approval && (
                  <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded">
                    Needs Approval
                  </span>
                )}
              </div>

              {config.description && (
                <p className="text-gray-600 mt-1" style={{ fontSize: '14px' }}>
                  {config.description}
                </p>
              )}

              {/* Mock upload button */}
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-700 text-white rounded-lg text-sm font-medium mb-2">
                  <UploadFileIcon style={{ fontSize: 18 }} />
                  Choose File to Upload
                </div>
                <p className="text-gray-500" style={{ fontSize: '12px' }}>
                  (Employee upload interface - preview only)
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer help text */}
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 rounded-b-lg">
        <div className="flex items-center gap-1.5 text-xs text-gray-600">
          <TipsAndUpdatesIcon style={{ fontSize: 16 }} />
          <span>Click the settings icon to configure this upload block</span>
        </div>
      </div>
    </div>
  );
};
